// Every custom GA4 event the site sends, in one place.
//
// Keeping the names here rather than scattering string literals through the
// components matters more than it looks: GA4 event names are not retroactive.
// A typo does not error — it silently creates a second, near-identical event
// that you only discover weeks later when the report is split in two.

import { trackEvent } from './googleAnalytics';
import { appStoreLink, googlePlayLink } from '../globalConstants';

/**
 * Where on the site a call to action was clicked. Reported as an event
 * parameter so the same event name answers "how many store clicks" and "which
 * section earns them".
 */
export type CtaPlacement =
    | 'navbar'
    | 'hero'
    | 'lead_section'
    | 'how_it_works'
    | 'duplicate_poker'
    | 'feedback_engine'
    | 'statistics'
    | 'play_vs_friends'
    | 'subscriptions'
    | 'footer'
    | 'unknown';

type Store = 'app_store' | 'google_play' | 'unknown';

function storeFromLink(link: string | null | undefined): Store {
    if (link === appStoreLink) {
        return 'app_store';
    }
    if (link === googlePlayLink) {
        return 'google_play';
    }
    return 'unknown';
}

/**
 * A visitor clicked through to the App Store or Play Store. This is the
 * conversion the whole marketing site exists to produce, so it is the one
 * event worth getting right.
 */
export function trackDownloadClick(link: string | null | undefined, placement: CtaPlacement): void {
    trackEvent('download_click', {
        store: storeFromLink(link),
        placement,
        link_url: link || '',
    });
}

/** A visitor followed the Instagram link in the footer. */
export function trackSocialClick(network: string, placement: CtaPlacement): void {
    trackEvent('social_click', { network, placement });
}

/**
 * The cookie banner was answered. Sent for both outcomes.
 *
 * A 'denied' event is not a contradiction: it carries no identifier and sets no
 * cookie, it is exactly the cookieless ping Consent Mode is designed around,
 * and without it you cannot tell a declining visitor from one who never saw the
 * banner — which is the number you need to know how much GA4 is missing.
 */
export function trackConsentDecision(choice: 'granted' | 'denied'): void {
    trackEvent('cookie_consent', { choice });
}
